import React from 'react';

import { bemBlock } from '../../modules/bem';
import { type GlyphName, Icon } from '../icon/Icon';
import { Layout } from '../layout/Layout';
import { Text } from '../text/Text';

import { getDataRole, getItemKey } from './Panel.helpers';
import { type DataRoleProps, type PanelIntent } from './Panel.types';

import './PanelAlert.less';

const block = bemBlock('n-Panel');

export type PanelAlertProps = React.HTMLAttributes<HTMLDivElement> &
  DataRoleProps & {
    icon?: GlyphName;
    intent?: PanelIntent;
    items?: React.ReactNode[];
  };

export const PanelAlert = React.forwardRef<HTMLDivElement, PanelAlertProps>(function PanelAlert(
  {
    children,
    className,
    dataRole,
    icon,
    intent = 'warning',
    items,
    'data-role': dataRoleAttribute,
    ...passProps
  },
  ref,
) {
  const glyph = icon ?? getAlertIcon(intent);

  if ((items === undefined || items.length === 0) && children === undefined) {
    return null;
  }

  return (
    <Layout.Element
      {...passProps}
      elementRef={ref}
      className={block({ element: 'alert', modifiers: { intent }, extra: className })}
      data-role={getDataRole(dataRole, dataRoleAttribute)}
    >
      {items?.map((item, index) => (
        <Layout.Element key={getItemKey(item, index)} className={block('alert-item')}>
          <Icon glyph={glyph} color={getIconColor(intent)} size="xs" />
          <Text size="sm">{item}</Text>
        </Layout.Element>
      ))}
      {children}
    </Layout.Element>
  );
});

function getAlertIcon(intent: PanelIntent): GlyphName {
  if (intent === 'success') {
    return 'check-circle';
  }

  if (intent === 'error') {
    return 'exclamation-circle';
  }

  if (intent === 'info') {
    return 'info-circle';
  }

  return 'exclamation-triangle';
}

function getIconColor(intent: PanelIntent): React.ComponentProps<typeof Icon>['color'] {
  if (intent === 'error') {
    return 'error';
  }

  if (intent === 'success') {
    return 'success';
  }

  if (intent === 'info') {
    return 'info';
  }

  return 'warning';
}
